import {readQuerySafe, writeFragmentsToCache, writeQuery} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';

import {BenchlingAppDefinitionGraphQL as AppDefinition} from '../../graphql-fragments/benchling-apps/__generated/BenchlingAppDefinitionGraphQL';
import {BenchlingAppGraphQL as AppInstallation} from '../../graphql-fragments/benchling-apps/__generated/BenchlingAppGraphQL';
import {AppdefinitionVersionResponse, shareAppDefinitionVersionWithAllowlist} from './AppDefinitionDAO';
import {
  createBenchlingAppDefinition,
  createBenchlingAppDefinitionFromManifest,
  installAppDefinitionVersionOnHomeTenant,
  updateBenchlingAppDefinition,
  updateBenchlingAppDefinitionVersion,
  updateBenchlingAppDefinitionVersionFromManifest,
} from './AppDefinitionDAO';
import {Audience} from './BenchlingAppDAO';
import {ListBenchlingAppDefinitionsQuery} from './developer/__generated/ListBenchlingAppDefinitionsQuery';
import {
  BENCHLING_APP_DEFINITIONS_LISTING_QUERY,
  BENCHLING_APP_LISTING_QUERY_PAGE_SIZE,
} from './developer/BenchlingAppsPage';

export async function shareAppDefinitionVersion(
  appDefinitionId: string,
  version: string,
  allowList: Array<string>
): Promise<AppdefinitionVersionResponse> {
  return await shareAppDefinitionVersionWithAllowlist({
    appDefinitionId,
    version,
    allowList,
  });
}

export async function createAppDefinition(data: {
  name: string;
  description: string;
  audience: Audience;
}): Promise<AppDefinition> {
  const response = await createBenchlingAppDefinition(data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingAppDefinition'), [response.benchlingAppDefinition]);
  addCreatedAppDefinitionToApolloCache(response.benchlingAppDefinition);
  return response.benchlingAppDefinition;
}

export async function createAppDefinitionFromManifest(data: {
  location: string;
  audience: Audience;
}): Promise<AppDefinition> {
  const response = await createBenchlingAppDefinitionFromManifest(data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingAppDefinition'), [response.benchlingAppDefinition]);
  addCreatedAppDefinitionToApolloCache(response.benchlingAppDefinition);
  return response.benchlingAppDefinition;
}

export async function updateAppDefinition(
  appDefinitionId: string,
  data: {
    webhookUrl?: string | null;
    publicKey?: string | null;
    webhookHeaders?: Array<{key: string; value: string}> | null;
  }
): Promise<AppDefinition> {
  const response = await updateBenchlingAppDefinition(appDefinitionId, data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingAppDefinition'), [response.benchlingAppDefinition]);
  return response.benchlingAppDefinition;
}

export async function updateAppDefinitionVersion(
  appDefinitionId: string,
  version: string | null,
  data: {
    name?: string;
    description?: string;
    allowList?: Array<string>;
    versionToInheritFrom?: string;
  }
): Promise<AppDefinition> {
  const response = await updateBenchlingAppDefinitionVersion(appDefinitionId, version, data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingAppDefinition'), [response.benchlingAppDefinition]);
  return response.benchlingAppDefinition;
}

export async function updateAppDefinitionVersionFromManifest(
  appDefinitionId: string,
  version: string | null,
  data: {
    location: string;
  }
): Promise<AppDefinition> {
  const response = await updateBenchlingAppDefinitionVersionFromManifest(appDefinitionId, version, data);
  writeFragmentsToCache(getFragmentWrapper('BenchlingAppDefinition'), [response.benchlingAppDefinition]);
  return response.benchlingAppDefinition;
}

export async function installVersionOnHomeTenant(
  appDefinitionId: string,
  version: string
): Promise<AppInstallation> {
  const response = await installAppDefinitionVersionOnHomeTenant(appDefinitionId, version);
  writeFragmentsToCache(getFragmentWrapper('BenchlingApp'), [response.benchlingApp]);
  return response.benchlingApp;
}

function addCreatedAppDefinitionToApolloCache(appDefinition: AppDefinition): void {
  const cachedAppDefinitions: ListBenchlingAppDefinitionsQuery | null = readQuerySafe({
    query: BENCHLING_APP_DEFINITIONS_LISTING_QUERY,
  });
  let newAppDefinitionsList = [];
  if (!cachedAppDefinitions) {
    return;
  }
  newAppDefinitionsList = [
    appDefinition,
    ...cachedAppDefinitions.benchlingAppDefinitionDeveloperListing.appDefinitions,
  ];
  newAppDefinitionsList = newAppDefinitionsList.slice(0, BENCHLING_APP_LISTING_QUERY_PAGE_SIZE);
  writeQuery(
    {query: BENCHLING_APP_DEFINITIONS_LISTING_QUERY},
    {
      benchlingAppDefinitionDeveloperListing: {
        appDefinitions: newAppDefinitionsList,
        __typename: 'BenchlingAppDefinitionListingNode',
      },
    }
  );
}
